
import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { WorkoutExercise } from '@/types/workout';
import { User } from '@supabase/supabase-js';
import { 
  checkCompletedWorkoutToday, 
  completeWorkout, 
  getDefaultWorkoutExercises, 
  getOrCreateTodaysWorkout, 
  recordUserActivity, 
  requestExerciseFeedback 
} from '@/services/workout-service';
import { fetchUserStreak, updateStreakAfterWorkout } from '@/services/streak-service';

export const useWorkout = (user: User | null) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{ [key: number]: string }>({});
  const [completed, setCompleted] = useState<number[]>([]);
  const [workoutProgress, setWorkoutProgress] = useState(0);
  const [feedback, setFeedback] = useState<{ [key: number]: string }>({});
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [streakCount, setStreakCount] = useState(1);
  const [isSubmittingWorkout, setIsSubmittingWorkout] = useState(false);
  const { toast } = useToast();
  
  const workoutExercises = getDefaultWorkoutExercises();
  const currentExercise = workoutExercises[currentExerciseIndex];

  // Fetch initial streak data when component mounts
  useEffect(() => {
    if (user?.id) {
      const loadStreakData = async () => {
        try {
          const streak = await fetchUserStreak(user.id);
          setStreakCount(streak);
        } catch (error) {
          console.error('Error loading streak data:', error);
        }
      };
      
      loadStreakData();
    }
  }, [user?.id]);

  // Track workout completion
  useEffect(() => {
    if (completed.length === workoutExercises.length && completed.length > 0) {
      recordWorkoutCompleted();
    }
  }, [completed.length, workoutExercises.length]);

  const recordWorkoutCompleted = async () => {
    if (!user?.id || isSubmittingWorkout) return;
    
    setIsSubmittingWorkout(true);
    
    try {
      // Check if today's workout already completed
      const { hasCompletedToday } = await checkCompletedWorkoutToday(user.id);
      
      if (hasCompletedToday) {
        setShowCelebration(true);
        setIsSubmittingWorkout(false);
        return;
      }
      
      // Get or create today's workout
      const { id: workoutId } = await getOrCreateTodaysWorkout(user.id);
      
      // Mark workout as completed
      await completeWorkout(workoutId, user.id);
      
      // Update user streak
      const newStreak = await updateStreakAfterWorkout(user.id);
      setStreakCount(newStreak);
      
      // Record activity for points
      await recordUserActivity(
        user.id, 
        'workout', 
        'Completed Daily AI Workout', 
        10
      );
      
      console.log('Workout recorded successfully!');
      toast({
        title: "Workout Complete!",
        description: "Your workout has been recorded and points added to your profile.",
      });
      
      setShowCelebration(true);
      
    } catch (error) {
      console.error('Error recording workout completion:', error);
      toast({
        title: "Error recording progress",
        description: "We couldn't save your workout completion. Your progress is still saved locally.",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingWorkout(false);
    }
  };

  const handleAnswerChange = (value: string) => {
    setUserAnswers({
      ...userAnswers,
      [currentExerciseIndex]: value
    });
  };

  const handleCompleteExercise = async () => {
    if (!completed.includes(currentExerciseIndex) && userAnswers[currentExerciseIndex]?.trim()) {
      const newCompleted = [...completed, currentExerciseIndex];
      setCompleted(newCompleted);
      setWorkoutProgress(Math.round((newCompleted.length / workoutExercises.length) * 100));
      
      await requestFeedback();
    } else if (!userAnswers[currentExerciseIndex]?.trim()) {
      toast({
        title: "Empty answer",
        description: "Please provide an answer before completing the exercise.",
        variant: "destructive"
      });
    }
  };

  const requestFeedback = async () => {
    const answer = userAnswers[currentExerciseIndex];
    if (!answer?.trim()) return;
    
    setIsLoadingFeedback(true);
    
    try {
      const exerciseFeedback = await requestExerciseFeedback(
        currentExercise.title,
        currentExercise.description,
        answer
      );
      
      setFeedback({
        ...feedback,
        [currentExerciseIndex]: exerciseFeedback
      });
      
      toast({
        title: "Feedback received",
        description: "AI has analyzed your response and provided feedback.",
      });
    } catch (error) {
      console.error('Error getting feedback:', error);
      toast({
        title: "Feedback error",
        description: "Unable to generate feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < workoutExercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    }
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
    }
  };

  const handleCloseCelebration = () => {
    setShowCelebration(false);
  };

  return {
    currentExerciseIndex,
    setCurrentExerciseIndex,
    workoutExercises,
    currentExercise,
    userAnswers,
    completed,
    workoutProgress,
    feedback,
    isLoadingFeedback,
    showCelebration,
    streakCount,
    handleAnswerChange,
    handleCompleteExercise,
    handleNextExercise,
    handlePreviousExercise,
    handleCloseCelebration
  };
};
